import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import MediaQuery from 'react-responsive';
import { formatCurrency } from '../Currency';

import { AccountValuesPropType } from './PropTypes';
import { LAYOUT, MIN_WIDTH } from '../Layout/enums';
import loadingGif from '../../assets/images/loading.gif';
import warning from '../../assets/images/warning.svg';

import './ValueChart.css';

// Chart data range selections
export const OPT_1D = '1d';
export const OPT_1W = '1w';
export const OPT_1M = '1m';
export const OPT_6M = '6m';
export const OPT_1Y = '1y';
export const OPT_ALL = 'all';

export const CHART_TYPE = Object.freeze({ ACCOUNT_VAL: 0, SECURITY: 1 });

export const prepJSONforChart = (values) => {
  const newLabels = [];
  const newData = [];

  if (values && values.length > 0) {
    for (let i = 0; i < values.length; i += 1) {
      const value = values[i];
      if (value.date && value.marketValue) {
        newLabels.push(moment(value.date));
        newData.push(parseFloat(value.marketValue));
      }
    }
  }

  return { labels: newLabels, data: newData };
};

const MissingChartData = () => (
  <div styleName="valueChart">
    <div styleName="missingChartData">
      <img
        styleName="missingDataImage"
        src={warning}
        alt="Missing Data"
      />
      <div styleName="missingDataMessage">
        Chart data failed to load.<br />Please try again in a few minutes.
      </div>
    </div>
  </div>
);

// Returns options to apply to value chart x axis
// since these options vary depending on selected range
const getXTimeForRangeOpt = (range, labels) => {
  let xTime = {};

  switch (range) {
    case OPT_1D:
      xTime = {
        min: moment(moment.max(labels)).subtract(1, 'days'),
        max: moment.max(labels),
        unit: 'hour',
        displayFormats: {
          hour: 'hA',
        },
      };
      break;
    case OPT_1W:
      xTime = {
        min: moment(moment.max(labels)).subtract(1, 'weeks'),
        max: moment.max(labels),
        unit: 'day',
        displayFormats: {
          day: 'MMM D',
        },
      };
      break;
    case OPT_1M:
      xTime = {
        min: moment(moment.max(labels)).subtract(1, 'months'),
        max: moment.max(labels),
        unit: 'week',
        displayFormats: {
          week: 'll',
        },
      };
      break;
    case OPT_6M:
      xTime = {
        min: moment(moment.max(labels)).subtract(6, 'months'),
        max: moment.max(labels),
        unit: 'month',
        displayFormats: {
          month: 'MMM YYYY',
        },
      };
      break;
    case OPT_1Y:
      xTime = {
        min: moment(moment.max(labels)).subtract(1, 'years'),
        max: moment.max(labels),
        unit: 'month',
        displayFormats: {
          month: 'MMM YYYY',
        },
      };
      break;
    default: // OPT_ALL
      xTime = {
        min: moment.min(labels),
        max: moment.max(labels),
        unit: 'year',
        displayFormats: {
          month: 'YYYY',
        },
      };
      break;
  }
  return xTime;
};

const shouldUseDetailValues = (range, detailValues) =>
  detailValues.length > 0 &&
  (range === OPT_1D || range === OPT_1M || range === OPT_1W);

const getState = (range, labels, data, chartType) => {
  const xTime = getXTimeForRangeOpt(range, labels);

  return ({
    activeRangePicker: range,
    data: {
      key: range,
      labels,
      datasets: [
        {
          label: chartType === CHART_TYPE.ACCOUNT_VAL ?
            'Securities' : 'Close',
          fill: false,
          lineTension: 0.5,
          backgroundColor: 'rgba(75,192,192,0.4)',
          borderColor: 'rgba(151, 151, 151, 1)',
          borderWidth: 1,
          borderCapStyle: 'butt',
          borderDash: [],
          borderDashOffset: 0.0,
          borderJoinStyle: 'miter',
          pointHoverRadius: 5,
          pointHoverBackgroundColor: 'rgba(151, 151, 151, 1)',
          pointHoverBorderColor: 'rgba(151, 151, 151, .1)',
          pointHoverBorderWidth: 2,
          pointRadius: 0,
          pointHitRadius: 10,
          data,
        },
      ],
    },
    options: {
      legend: {
        display: false,
      },
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        easing: 'easeOutQuint',
      },
      tooltips: {
        callbacks: {
          label: (tti, ds) => (
            `${ds.datasets[tti.datasetIndex].label}: ${formatCurrency(tti.yLabel)}`
          ),
        },
      },
      scales: {
        yAxes: [{
          gridLines: {
            drawBorder: false,
            display: false,
          },
          ticks: {
            display: true,
            padding: 10,
            maxTicksLimit: 3,
            fontFamily: 'Source Sans Pro',
            fontSize: 16,
            fontColor: '#767676',
            callback: (value) => {
              if (value >= 1000000.00) { return `$${value / 1000000.00}m`; } else
              if (value >= 1000.00) { return `$${value / 1000.00}k`; }
              return `$${value}`;
            },
          },
        }],
        xAxes: [{
          gridLines: {
            drawBorder: false,
            display: false,
          },
          type: 'time',
          position: 'bottom',
          time: xTime,
          ticks: {
            display: true,
            padding: 6,
            maxTicksLimit: 10,
            fontFamily: 'Source Sans Pro',
            fontSize: 16,
            fontColor: '#767676',
          },
        }],
      },
    },
  });
};

class ValueChart extends Component {
  constructor(props) {
    super(props);

    if (shouldUseDetailValues(props.initialRange, props.detailValues)) {
      const chartData = prepJSONforChart(props.detailValues);
      const { labels, data } = chartData;

      this.state = getState(props.initialRange, labels, data, props.chartType);
    } else {
      const chartData = prepJSONforChart(props.values);
      const { labels, data } = chartData;

      this.state = getState(props.initialRange, labels, data, props.chartType);
    }
  }

  componentDidMount() {
    this.changeRange(this.state.activeRangePicker);
  }

  componentWillReceiveProps(props) {
    if (shouldUseDetailValues(this.state.activeRangePicker, props.detailValues)) {
      const chartData = prepJSONforChart(props.detailValues);
      const { labels, data } = chartData;

      this.setState(getState(this.state.activeRangePicker, labels, data, this.props.chartType));
    } else {
      const chartData = prepJSONforChart(props.values);
      const { labels, data } = chartData;

      this.setState(getState(this.state.activeRangePicker, labels, data, this.props.chartType));
    }
  }

  getChart(layout, disabled) {
    let fontSize = 16;
    let display = true;
    const borderColor = disabled ? 'rgba(151, 151, 151, .2)' : 'rgba(151, 151, 151, 1)';

    switch (layout) {
      case LAYOUT.TAB:
        fontSize = 14;
        break;
      case LAYOUT.MOB:
        fontSize = 12;
        display = false;
        break;
      default:
        break;
    }
    return (
      <div styleName="valueChart">
        { disabled ? (
          <div styleName="loadingChart">
            <img
              styleName="loadingChartImage"
              src={loadingGif}
              alt="Loading Data"
            />
            Loading chart data
          </div>
          ) : (
            <Line
              data={{
                ...this.state.data,
                key: disabled,
                datasets: [
                  {
                    ...this.state.data.datasets[0],
                    borderColor,
                  },
                ],
              }}
              options={{
                ...this.state.options,
                scales: {
                  ...this.state.options.scales,
                  yAxes: [{
                    ...this.state.options.scales.yAxes[0],
                    ticks: {
                      ...this.state.options.scales.yAxes[0].ticks,
                      fontSize,
                      display,
                    },
                  }],
                  xAxes: [{
                    ...this.state.options.scales.xAxes[0],
                    ticks: {
                      ...this.state.options.scales.xAxes[0].ticks,
                      fontSize,
                      display,
                    },
                  }],
                },
              }}
            />
          )
        }
      </div>
    );
  }
  changeRange = (toRange) => {
    // QuoteMedia getChartData will only return day starting 365000 days ago
    // If the start value is more than that the QuoteMedia returns no chart data.
    let value = (365 * 100) - 1; // Subtract 1 day as buffer for timezones
    let units = 'days';
    switch (toRange) {
      case OPT_1D:
        value = 1;
        units = 'days';
        break;
      case OPT_1W:
        value = 1;
        units = 'week';
        break;
      case OPT_1M:
        value = 1;
        units = 'months';
        break;
      case OPT_6M:
        value = 6;
        units = 'months';
        break;
      case OPT_1Y:
        value = 1;
        units = 'years';
        break;
      case OPT_ALL:
      default:
    }

    // Need to set activeRangePicker immediately so componentWillReceiveProps
    // will use range. componentWillReceiveProps is triggered because onRangeChange
    // updates isChartDataLoading prop
    this.state.activeRangePicker = toRange;
    this.props.onRangeChange(value, units);

    if (shouldUseDetailValues(toRange, this.props.detailValues)) {
      const chartData = prepJSONforChart(this.props.detailValues);
      const { labels, data } = chartData;

      this.setState(getState(toRange, labels, data, this.props.chartType));
    } else {
      const chartData = prepJSONforChart(this.props.values);
      const { labels, data } = chartData;

      this.setState(getState(toRange, labels, data, this.props.chartType));
    }
    // const { labels } = this.state.data;

    // const newOptions = getXTimeForRangeOpt(toRange, labels);

    // this.setState({
    //   ...this.state,
    //   data: {
    //     ...this.state.data,
    //     key: toRange,
    //   },
    //   activeRangePicker: toRange,
    //   options: {
    //     ...this.state.options,
    //     scales: {
    //       ...this.state.options.scales,
    //       xAxes: [{
    //         ...this.state.options.scales.xAxes[0],
    //         time: newOptions,
    //       }],
    //     },
    //   },
    // });
  }

  ChartRangePicker = (activeOption, groupStyle, activeStyle, inactiveStyle, disabled) => (
    <div styleName={groupStyle}>
      <input
        id="OPT_1D"
        type="button"
        alt="One Day"
        styleName={activeOption === OPT_1D ? activeStyle : inactiveStyle}
        value={OPT_1D}
        onClick={() => this.changeRange(OPT_1D)}
        disabled={disabled}
      />
      <input
        id="OPT_1W"
        type="button"
        alt="One Week"
        styleName={activeOption === OPT_1W ? activeStyle : inactiveStyle}
        value={OPT_1W}
        onClick={() => this.changeRange(OPT_1W)}
        disabled={disabled}
      />
      <input
        id="OPT_1M"
        type="button"
        alt="One Month"
        styleName={activeOption === OPT_1M ? activeStyle : inactiveStyle}
        value={OPT_1M}
        onClick={() => this.changeRange(OPT_1M)}
        disabled={disabled}
      />
      <input
        id="OPT_6M"
        type="button"
        alt="Six Months"
        styleName={activeOption === OPT_6M ? activeStyle : inactiveStyle}
        value={OPT_6M}
        onClick={() => this.changeRange(OPT_6M)}
        disabled={disabled}
      />
      <input
        id="OPT_1Y"
        type="button"
        alt="One Year"
        styleName={activeOption === OPT_1Y ? activeStyle : inactiveStyle}
        value={OPT_1Y}
        onClick={() => this.changeRange(OPT_1Y)}
        disabled={disabled}
      />
      <input
        id="OPT_ALL"
        type="button"
        alt="All Time"
        styleName={activeOption === OPT_ALL ? activeStyle : inactiveStyle}
        value="All"
        onClick={() => this.changeRange(OPT_ALL)}
        disabled={disabled}
      />
    </div>
  )

  render() {
    const { mediaQueryValues, chartType, isChartDataLoading } = this.props;
    return (
      <div >
        {chartType === CHART_TYPE.ACCOUNT_VAL ?
          <div>
            <MediaQuery
              minWidth={MIN_WIDTH.DESK}
              values={mediaQueryValues}
            >
              { this.state.data ? (
                <div>
                  {this.ChartRangePicker(this.state.activeRangePicker, 'chartRangePicker', 'rangeOptActive', 'rangeOpt', isChartDataLoading)}
                  {this.getChart(LAYOUT.DESK, isChartDataLoading)}
                </div>
                ) : (
                  <MissingChartData />
                )
              }
            </MediaQuery>
            <MediaQuery
              minWidth={MIN_WIDTH.TAB}
              maxWidth={MIN_WIDTH.DESK - 1}
              values={mediaQueryValues}
            >
              { this.state.data ? (
                <div>
                  {this.ChartRangePicker(this.state.activeRangePicker, 'chartRangePickerTab', 'rangeOptActiveTab', 'rangeOptTab', isChartDataLoading)}
                  {this.getChart(LAYOUT.TAB, isChartDataLoading)}
                </div>
                ) : (
                  <MissingChartData />
                )
              }
            </MediaQuery>
            <MediaQuery
              maxWidth={MIN_WIDTH.TAB - 1}
              values={mediaQueryValues}
            >
              { this.state.data ? (
                <div>
                  {this.ChartRangePicker(this.state.activeRangePicker, 'chartRangePickerMob', 'rangeOptActiveMob', 'rangeOptMob', isChartDataLoading)}
                  {this.getChart(LAYOUT.MOB, isChartDataLoading)}
                </div>

                ) : (
                  <MissingChartData />
                )
              }
            </MediaQuery>
          </div> :
          <div>
            <MediaQuery
              minWidth={MIN_WIDTH.TAB}
              values={mediaQueryValues}
            >
              { this.state.data ? (
                <div>
                  {this.ChartRangePicker(
                     this.state.activeRangePicker,
                     'chartRangePickerTab',
                     'rangeOptActiveTab',
                     'rangeOptTab',
                     isChartDataLoading,
                  )}
                  {this.getChart(LAYOUT.TAB, isChartDataLoading)}
                </div>
                ) : (
                  <MissingChartData />
                )
              }
            </MediaQuery>
            <MediaQuery
              maxWidth={MIN_WIDTH.TAB - 1}
              values={mediaQueryValues}
            >
              { this.state.data ? (
                <div>
                  {this.ChartRangePicker(
                    this.state.activeRangePicker,
                    'chartRangePickerMob',
                    'rangeOptActiveMob',
                    'rangeOptMob',
                    isChartDataLoading,
                  )}
                  {this.getChart(LAYOUT.MOB, isChartDataLoading)}
                </div>
                ) : (
                  <MissingChartData />
                )
              }
            </MediaQuery>
          </div>}
      </div>
    );
  }
}

ValueChart.propTypes = {
  isChartDataLoading: PropTypes.bool,
  values: AccountValuesPropType,
  detailValues: AccountValuesPropType,
  chartType: PropTypes.number,
  mediaQueryValues: PropTypes.shape({}),
  initialRange: PropTypes.string,
  onRangeChange: PropTypes.func,
};

ValueChart.defaultProps = {
  isChartDataLoading: false,
  values: [],
  detailValues: [],
  chartType: CHART_TYPE.ACCOUNT_VAL,
  mediaQueryValues: {},
  initialRange: OPT_1Y,
  onRangeChange: () => {},
};

export default ValueChart;



// WEBPACK FOOTER //
// ./src/components/Portfolio/ValueChart.js